import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * GlobalSEO — reads the live SEOConfig (plus any per-page overrides) from
 * /api/homepage/public and injects matching <meta> tags via react-helmet-async.
 *
 * Per-page SEO: config.page_seo is a dict keyed by URL path (e.g. "/program").
 *   When the current pathname matches a key, that path's fields are merged
 *   OVER the global `seo` block. Any field left blank on the override falls
 *   back to the global value, which falls back to a hard-coded default.
 *
 * Important caveat (surfaced in the admin panel too):
 *   - Google and other modern crawlers DO execute JavaScript, so helmet-rendered
 *     tags take effect within hours/days of a change AND per-page overrides work.
 *   - iMessage, Slack, Facebook, WhatsApp previewers do NOT run JS. They parse
 *     the static /app/frontend/public/index.html at crawl time — those get the
 *     site-wide defaults regardless of path. Changing them requires a redeploy.
 */
const GlobalSEO = () => {
  const location = useLocation();
  const [config, setConfig] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/homepage/public`);
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setConfig(data?.config || null);
      } catch { /* non-fatal — static index.html tags remain */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!config?.seo) return null;

  const seo = config.seo || {};
  // Normalize pathname: strip trailing slash so "/program/" matches "/program".
  const pathname = (location?.pathname || '/').replace(/\/+$/, '') || '/';
  const override = (config.page_seo && config.page_seo[pathname]) || {};

  // Fallback chain for every rendered tag:
  //   per-page override → global seo → sensible hard-coded default
  const pick = (ovKey, seoKey, fallback = '') =>
    override[ovKey] || seo[seoKey] || fallback;

  const title = pick('page_title', 'page_title', 'World Nuclear Congress 2027');
  const description = pick('meta_description', 'meta_description', '');
  const keywords = pick('keywords', 'keywords', '');
  const canonicalBase = (seo.canonical_url || '').replace(/\/+$/, '');
  // Per-page canonicals use the base + the current pathname.
  const canonical = canonicalBase ? `${canonicalBase}${pathname === '/' ? '' : pathname}` : '';
  const favicon = seo.favicon_url || '';

  const ogTitle = override.og_title || seo.og_title || title;
  const ogDesc = override.og_description || seo.og_description || description;
  const ogImage = override.og_image || seo.og_image || '';
  const ogImageAlt = override.og_image_alt || seo.og_image_alt || title;
  const siteName = seo.site_name || 'World Nuclear Congress';

  const twitterCard = seo.twitter_card || 'summary_large_image';
  const twitterHandle = seo.twitter_handle || '';

  return (
    <Helmet>
      <title>{title}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      {canonical && <link rel="canonical" href={canonical} />}
      {favicon && <link rel="icon" href={favicon} />}

      {/* Open Graph — iMessage, Slack, Facebook, LinkedIn, WhatsApp */}
      <meta property="og:type" content="website" />
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={ogTitle} />
      {ogDesc && <meta property="og:description" content={ogDesc} />}
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImage && <meta property="og:image:alt" content={ogImageAlt} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={ogTitle} />
      {ogDesc && <meta name="twitter:description" content={ogDesc} />}
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      {twitterHandle && <meta name="twitter:site" content={twitterHandle} />}
    </Helmet>
  );
};

export default GlobalSEO;
